import {
  EMPTY_INACTIVE_LOOKUP_KEYS,
  EMPTY_INACTIVE_LOOKUP_TOOLTIPS,
  INACTIVE_LOOKUP_KEYS,
  InactiveLookupTooltips,
  SERIALIZED_EMPTY_INACTIVE_LOOKUP_TOOLTIPS,
} from './constants'

export type { InactiveLookupTooltips } from './constants'
export {
  EMPTY_INACTIVE_LOOKUP_KEYS,
  EMPTY_INACTIVE_LOOKUP_TOOLTIPS,
  INACTIVE_LOOKUP_KEYS,
  INACTIVE_LOOKUP_TOOLTIPS,
  SERIALIZED_EMPTY_INACTIVE_LOOKUP_TOOLTIPS,
} from './constants'

const parsedTooltipsCache = new Map<string, InactiveLookupTooltips>()

// Validates that a value is a string-keyed map of tooltip messages.
const isInactiveLookupTooltips = (
  value: unknown
): value is InactiveLookupTooltips =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.values(value)?.every((tooltip) => typeof tooltip === 'string')

// Serializes inactive lookup tooltips to a JSON string for DataTypeStore rows.
export const serializeInactiveLookupTooltips = (
  tooltips: InactiveLookupTooltips
): string => {
  if (!tooltips || Object.keys(tooltips)?.length === 0) {
    return SERIALIZED_EMPTY_INACTIVE_LOOKUP_TOOLTIPS
  }

  try {
    return JSON.stringify(tooltips)
  } catch {
    return SERIALIZED_EMPTY_INACTIVE_LOOKUP_TOOLTIPS
  }
}

// Safely deserializes inactive lookup tooltips from a row value.
export const parseInactiveLookupTooltips = (
  value: unknown
): InactiveLookupTooltips => {
  if (!value) return EMPTY_INACTIVE_LOOKUP_TOOLTIPS

  if (isInactiveLookupTooltips(value)) {
    return value
  }

  if (typeof value !== 'string') return EMPTY_INACTIVE_LOOKUP_TOOLTIPS

  if (value === SERIALIZED_EMPTY_INACTIVE_LOOKUP_TOOLTIPS) {
    return EMPTY_INACTIVE_LOOKUP_TOOLTIPS
  }

  const cachedTooltips = parsedTooltipsCache?.get(value)
  if (cachedTooltips) return cachedTooltips

  try {
    const parsed: unknown = JSON.parse(value)
    if (!isInactiveLookupTooltips(parsed)) {
      return EMPTY_INACTIVE_LOOKUP_TOOLTIPS
    }

    parsedTooltipsCache.set(value, parsed)
    return parsed
  } catch {
    return EMPTY_INACTIVE_LOOKUP_TOOLTIPS
  }
}

// Reads and normalizes inactive single-lookup field paths attached to a table row.
export const getInactiveLookupKeysFromRow = (
  row: unknown
): readonly string[] => {
  const inactiveKeys = (row as Record<string, unknown> | undefined)?.[
    INACTIVE_LOOKUP_KEYS
  ]

  if (!Array.isArray(inactiveKeys) || inactiveKeys.length === 0) {
    return EMPTY_INACTIVE_LOOKUP_KEYS
  }

  if (inactiveKeys?.every((key) => typeof key === 'string')) {
    return inactiveKeys
  }

  const filteredKeys = inactiveKeys?.filter(
    (key): key is string => typeof key === 'string'
  )

  return filteredKeys?.length > 0 ? filteredKeys : EMPTY_INACTIVE_LOOKUP_KEYS
}
